import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Avatar,
  Radio,
  ClickAwayListener,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "../../styles/actionItems.css";
import { getAvatarColor, getAvatarInitials } from "../../utils/utils";
import CommonDatePicker from "./CommonDatePicker";
import dayjs from "dayjs";
import DeleteIcon from "../../assets/common/delete-icon.svg";
import { createPortal } from "react-dom";
import CustomDropdown from "./CustomDropdown";

interface DropdownOption {
  label: string;
  value: string | null;
  icon?: string | React.ReactNode;
  color?: string;
  pictureUrl?: string;
  type?: "user" | "space";
  email?: string;
}

interface DropdownOptionWithDisplay extends DropdownOption {
  __displayLabel?: string; // only used for UI
}
interface BulkUpdateAction {
  label: string;
  icon: React.ReactNode;
  type?: "select" | "search" | "custom" | "date";
  options?: DropdownOption[];
  onSelect?: (value: DropdownOption | DropdownOption[]) => void;
  tooltip?: string;
  disabled?: boolean;
  renderComponent?: React.ReactNode;
  hideLabel?: boolean;
  showAvatar?: boolean;
  key: string;
  multi?: boolean;
}

interface BulkUpdateBarProps {
  visible?: boolean;
  sections?: BulkUpdateAction[];
  onClose?: () => void;
  onApply?: () => void;
  onDelete?: () => void;
  className?: string;
  selectedActionItemIds?: any[];
  selectedValues: Record<string, DropdownOption| DropdownOption[] | null>;
  setSelectedValues: React.Dispatch<
    React.SetStateAction<Record<string, DropdownOption | DropdownOption[] | null>>
  >;
  minDate?: string | null;
  onSubscribeToggle?: () => void;
}

const BulkUpdateBar: React.FC<BulkUpdateBarProps> = ({
  visible = false,
  sections = [],
  onClose,
  className = "",
  selectedActionItemIds,
  selectedValues,
  setSelectedValues,
  minDate,
  onApply,
  onDelete,
}) => {

  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [_searchValue, setSearchValue] = useState("");
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // inside the component, after the other hooks
  const hasAnySelection = Object.values(selectedValues).some(
    (v) => v !== null && v !== undefined
  );
  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setActiveSection(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!selectedActionItemIds || selectedActionItemIds.length === 0) {
      setActiveSection(null);
    }
  }, [selectedActionItemIds]);
  
  const renderDropdown = (section: BulkUpdateAction) => {
    const isMulti = section.multi === true;
    const currentValue = selectedValues[section.key];

    return (
      <CustomDropdown
        items={section.options || []}
        value={currentValue || (isMulti ? [] : null)}
        multiple={isMulti}
        placeholder={section.key === "account" ? "Search by account" : "Search by name"}
        showSearch={section.type === "search"}
        showAllWhenNoSearch={section.type !== "search"}
        hideList={false}
        isSubscriber={section.key === "subscribe"}
        groupBy={
          section.key === "subscribe"
            ? (item) => (item.type === "space" ? "Spaces" : "Users")
            : undefined
        }
        getKey={(item) => item.value ?? ""}
        filterFn={(item, search) =>
          item.label.toLowerCase().includes(search.toLowerCase())
        }
        maxHeight = {300}
        renderItem={(item) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {section.showAvatar && (
              <Avatar
                src={item.pictureUrl || ""}
                alt={item.label}
                variant={item.type === "space" ? "rounded" : "circular"}
                sx={{
                  width: 20,
                  height: 20,
                  bgcolor: !item.pictureUrl ? getAvatarColor( item?.email || item.label) : undefined,
                  fontSize: 10,
                }}
              >
                {!item.pictureUrl && getAvatarInitials( item?.email || item.label)}
              </Avatar>
            )}

            {!section.showAvatar && item.icon && (
              typeof item.icon === "string" ? (
                <img src={item.icon} alt="" width={18} height={18} />
              ) : (
                item.icon
              )
            )}

            {!section.showAvatar && item.color && (
              typeof item.color === "string" && (
                <Radio checked={true} className={`status-radio ${item.color}`}
                  sx={{ 
                    p: 0.5, 
                    mr:1,
                    color: item.color,
                    "&.Mui-checked": {color: item.color},
                  }} 
                />
              )
            )}

            <Typography>{item.label}</Typography>
          </Box>
        )}
        onChange={(val) => {
          setSelectedValues((prev) => ({
            ...prev,
            [section.key]: val,
          }));

          if (section.onSelect) {
            section.onSelect(val as any);
          }
        }}
        onClose={() => setActiveSection(null)}
      />
    );
  };

  if (!visible) {
    return <></>;
  }
  return (
    <Box className={`bulk-update-container ${className}`}>
      <Box className="bulk-update-inner">
        {/* Left side - selected summary */}
        <Box className="bulk-update-left">
          <Box className="selected-task-box">
            <Typography className="selected-task-text">
              {selectedActionItemIds?.length || 0} Tasks Selected
            </Typography>
            <IconButton onClick={onClose} className="close-btn">
              <CloseIcon className="close-btn" onClick={onClose} />
            </IconButton>
          </Box>
          </Box>
          <Box className="bulk-update-middle-wrapper">
            <Box className="bulk-update-middle">
              {sections.map((section, index) => {
                const sectionKey = section.key; // use the real key
                const isActive = activeSection === section.key;
                const currentValue = selectedValues[section.key];
                const singleValue = Array.isArray(currentValue) ? null : currentValue?.value ?? null;

                const handleClick = () => {
                  setActiveSection((prev) => (prev === section.key ? null : section.key));
                };                

                return (
                  <Box key={index} className="bulk-update-section">
                    <Tooltip
                      title={section.tooltip || section.label}
                      arrow
                      placement="top"
                    >
                      <Box
                        ref={(el) => {
                          sectionRefs.current[sectionKey] =
                            el as HTMLDivElement | null;
                        }}
                        className={`bulk-update-option ${isActive ? "active" : ""}`}
                        onClick={handleClick}
                      >
                        {/* Always show section icon/avatar */}
                        {typeof section.icon === "string" ? (
                          <img
                            src={section.icon}
                            alt={section.label}
                            width={18}
                            height={18}
                            style={{ marginRight: 6 }}
                          />
                        ) : (
                          section.icon
                        )}
                        <Typography className="bulk-update-label">
                          {section.label}
                        </Typography>
                      </Box>
                    </Tooltip>

                    {/* POP-UP CONTENT */}
                    {isActive && (
                      <>
                        {/* DATE PICKER */}
                        {section.type === "date" && (
                          <CommonDatePicker
                            value={singleValue}
                            onChange={(isoDate) => {
                              const displayLabel = isoDate
                                ? dayjs(isoDate).format("DD MMM YY")
                                : "No due date";
                              const storedOption: DropdownOptionWithDisplay = {
                                label: section.label, // <-- original label (never changed)
                                value: isoDate,
                                // extra field just for UI
                                __displayLabel: displayLabel,
                              };
                              section.onSelect?.(storedOption);
                              setSelectedValues((prev) => ({
                                ...prev,
                                [section.key]: storedOption,
                              }));
                              setActiveSection(null);
                            }}
                            onClose={() => setActiveSection(null)}
                            minDate={minDate}
                            anchorEl={
                              sectionRefs.current[sectionKey] ?? undefined
                            }
                          />
                        )}

                        {/* NON-DATE DROPDOWNS → render outside bulk update bar */}
                        {section.type !== "date" &&
                          Array.isArray(section.options) &&
                          section.options.length > 0 &&
                          sectionRefs.current[sectionKey] &&
                          createPortal(
                            <ClickAwayListener onClickAway={() => setActiveSection(null)}>
                            <Box
                            className="bulk-update-dropdown"
                              sx={{
                                position: "absolute",
                                top:
                                  sectionRefs.current[sectionKey]?.getBoundingClientRect().bottom +
                                  window.scrollY +
                                  20,
                                left:
                                  sectionRefs.current[sectionKey]?.getBoundingClientRect().left,
                                zIndex: 2000, // above the bar and wrappers
                              }}
                            >
                              {renderDropdown(section)}
                            </Box>
                            </ClickAwayListener>,
                            document.body
                          )}
                          {/* CUSTOM COMPONENT DROPDOWN  */}
                          {isActive && section.type === "custom" &&
                            sectionRefs.current[sectionKey] &&
                            createPortal(
                              <Box
                                sx={{
                                  position: "absolute",
                                  top:
                                    sectionRefs.current[sectionKey]?.getBoundingClientRect().bottom +
                                    window.scrollY +
                                    4,
                                  left:
                                    sectionRefs.current[sectionKey]?.getBoundingClientRect().left,
                                  zIndex: 2000,
                                  width: "280px" // optional
                                }}
                              >
                                <Box className="bulk-update-dropdown">
                                  {section.renderComponent}
                                </Box>
                              </Box>,
                              document.body
                            )
                          }
                      </>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>

        {/* Right side – Apply + Delete */}
        <Box className="bulk-update-right">
          {/* Apply button */}
          <button
            className={`bulk-update-apply-btn ${
              hasAnySelection ? "active" : "disabled"
            }`}
            disabled={!hasAnySelection}
            onClick={() => {
              if (onApply) onApply();
              setSearchValue("");
            }}
          >
            Apply
          </button>

          {/* Delete icon */}
          {onClose && (
            <Tooltip title="Delete Task" placement="top" arrow>
              <img
                src={DeleteIcon}
                alt="delete"
                className="delete-btn"
                onClick={onDelete}
              />
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default BulkUpdateBar;
